/**
 * Категории витрины: `GET/POST /admin/categories`, `GET/PUT/DELETE /admin/categories/{id}`,
 * обложка — `POST/DELETE /admin/categories/{id}/cover`.
 *
 * Порядок задаёт поле `sort` — меньше идёт первым. Перетаскивание строк
 * сохраняется по одной категории за раз: общей ручки «сохранить порядок» нет.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mediaUrl, type ApiClient, type components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'

type CategoryDto = components['schemas']['Category']

export interface Category {
  id: number
  name: string
  nameUz: string
  desc: string
  descUz: string
  /**
   * Английские тексты экран не показывает, но возвращает как пришли: сервер
   * сохраняет категорию целиком, и без них каждое сохранение стирало бы их.
   */
  nameEn: string
  descEn: string
  /** Кэшбэк категории, % (R-03). */
  cb: number
  sort: number
  active: boolean
  /** Сколько товаров видно в категории — считает сервер. */
  products: number
  /** Обложка: ключ файла, пустая строка — обложки нет. Нужен только для записи. */
  cover: string
  /** Готовый адрес обложки от сервера; пустая строка — обложки нет. */
  coverUrl: string
}

export function toCategory(dto: CategoryDto): Category {
  return {
    id: dto.id ?? 0,
    name: dto.name_ru,
    nameUz: dto.name_uz ?? '',
    desc: dto.description_ru ?? '',
    descUz: dto.description_uz ?? '',
    nameEn: dto.name_en ?? '',
    descEn: dto.description_en ?? '',
    cb: dto.cashback_pct ?? 0,
    sort: dto.sort ?? 0,
    active: dto.is_active ?? true,
    products: dto.product_count ?? 0,
    cover: dto.cover_key ?? '',
    coverUrl: mediaUrl(dto.cover_url ?? '', env.apiBaseUrl),
  }
}

export function toCategoryInput(category: Category): components['schemas']['CategoryInput'] {
  return {
    name_ru: category.name,
    name_uz: category.nameUz,
    description_ru: category.desc,
    description_uz: category.descUz,
    name_en: category.nameEn,
    description_en: category.descEn,
    cashback_pct: category.cb,
    cover_key: category.cover || null,
    sort: category.sort,
    is_active: category.active,
  }
}

export async function fetchCategories(api: ApiClient): Promise<Category[]> {
  const page = await api.get<{ items?: CategoryDto[] }>('/admin/categories')
  return (page.items ?? []).map(toCategory)
}

export async function saveCategory(api: ApiClient, category: Category): Promise<Category> {
  const body = toCategoryInput(category)
  const dto = category.id
    ? await api.put<CategoryDto>(`/admin/categories/${category.id}`, body)
    : await api.post<CategoryDto>('/admin/categories', body)
  return toCategory(dto)
}

export function useCategories() {
  const api = useApi()
  return useQuery({ queryKey: keys.categories, queryFn: () => fetchCategories(api) })
}

export function useSaveCategory() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (category: Category) => saveCategory(api, category),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories }),
  })
}

/**
 * Сохранить несколько категорий разом. Общей ручки «сохранить порядок» у API
 * нет, поэтому перестановка — это две обычные записи; ждём обе, иначе экран
 * перечитал бы список на середине перестановки и показал её наполовину.
 */
export function useSaveMany() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (list: Category[]) => Promise.all(list.map((c) => saveCategory(api, c))),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories }),
  })
}

export function useDeleteCategory() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/admin/categories/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories }),
  })
}

/** Самая большая обложка, которую примет сервер: 5 МБ. */
export const COVER_MAX_BYTES = 5 * 1024 * 1024

/**
 * Обложка категории. Та же загрузка, что у фото товара: файл уходит полем
 * формы `file`, тип сервер определяет по байтам. Ответ — категория с новым
 * `cover_url`: адрес собирать самим нельзя, файлы переедут в хранилище.
 * `['categories']` накрывает и список, и карточку категории.
 */
export function useUploadCover() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const form = new FormData()
      form.append('file', file)
      return toCategory(await api.post<CategoryDto>(`/admin/categories/${id}/cover`, form))
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories }),
  })
}

/** Убрать обложку: плитка в приложении вернётся к виду по умолчанию. */
export function useRemoveCover() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) =>
      toCategory(await api.del<CategoryDto>(`/admin/categories/${id}/cover`)),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories }),
  })
}
